import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProfileTile } from "@/pages/Dashboard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { JHARKHAND_DISTRICTS, RELIGIONS, EDUCATION, MARITAL, blocksFor } from "@/data/jharkhand";
import { SlidersHorizontal, Search, BadgeCheck, ChevronLeft, ChevronRight, X, ArrowUpDown } from "lucide-react";

import { useAuth } from "@/context/AuthContext";

const ANY = "__any__";
const PAGE_SIZE = 12;

const INCOME_OPTIONS = [
  "No Income",
  "Below 1 Lakh",
  "1 - 3 Lakhs",
  "3 - 5 Lakhs",
  "5 - 10 Lakhs",
  "10 - 20 Lakhs",
  "Above 20 Lakhs",
];

const SORT_OPTIONS = [
  { value: "last_active", label: "Most Active" },
  { value: "newest", label: "Newest Members" },
  { value: "age_asc", label: "Age: Youngest First" },
  { value: "age_desc", label: "Age: Oldest First" },
];

const DEFAULT_FILTERS = {
  districts: [],
  block: ANY,
  religion: ANY,
  education: ANY,
  marital_status: ANY,
  min_age: 18,
  max_age: 60,
  caste: "",
  income: ANY,
  manglik: ANY,
  verified_only: false,
  sort_by: "last_active",
  q: "",
};

export default function Browse() {
  const { user } = useAuth();

  // Legal minimum age in India: Bride (Female) = 18, Groom (Male) = 21
  const defaultMinAge = useMemo(() => {
    return (user?.gender || "").toLowerCase() === "female" ? 21 : 18;
  }, [user?.gender]);
  const [searchParams] = useSearchParams();
  const verifiedOnlyParam = searchParams.get("verified") === "true";

  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    verified_only: verifiedOnlyParam,
  });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const userRelSetRef = useRef(false);

  // Set default religion filter based on logged-in user profile
  useEffect(() => {
    if (filters.min_age < defaultMinAge) {
      setFilters((f) => ({ ...f, min_age: Math.max(defaultMinAge, f.min_age), max_age: Math.max(defaultMinAge + 1, f.max_age) }));
    }
  }, [defaultMinAge]);

  useEffect(() => {
    if (user && !userRelSetRef.current) {
      userRelSetRef.current = true;
      const userReligion = user.religion || user.partner_pref?.religion;
      if (userReligion && filters.religion === ANY) {
        const updated = { ...filters, religion: userReligion };
        setFilters(updated);
        load(0, updated);
      }
    }
  }, [user]);

  // Debounce timer ref for text search
  const searchDebounce = useRef(null);

  const load = useCallback(async (toPage = 0, overrideFilters = null) => {
    const f = overrideFilters || filters;
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: toPage * PAGE_SIZE,
        ignore_pref: true, // always bypass partner_pref defaults when user is browsing
        sort_by: f.sort_by,
      };

      if (f.districts.length > 0) params.district = f.districts.join(",");
      if (f.block && f.block !== ANY) params.block = f.block;
      if (f.religion && f.religion !== ANY) params.religion = f.religion;
      if (f.education && f.education !== ANY) params.education = f.education;
      if (f.marital_status && f.marital_status !== ANY) params.marital_status = f.marital_status;
      if (f.manglik && f.manglik !== ANY) params.manglik = f.manglik;
      if (f.income && f.income !== ANY) params.income = f.income;
      if (f.min_age !== 18 || f.max_age !== 60) {
        params.min_age = f.min_age;
        params.max_age = f.max_age;
      }
      if (f.caste && f.caste.trim()) params.caste = f.caste.trim();
      if (f.verified_only) params.verified_only = true;
      if (f.q && f.q.trim()) params.q = f.q.trim();

      const { data } = await api.get("/browse", { params });
      setResults(data.results || []);
      setTotal(data.total || 0);
      setPage(toPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial load
  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply when non-text filters change
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const curr = filters;
    // Check if only q changed (handled by debounce below)
    const nonQChanged = (
      prev.districts.join(",") !== curr.districts.join(",") ||
      prev.block !== curr.block ||
      prev.religion !== curr.religion ||
      prev.education !== curr.education ||
      prev.marital_status !== curr.marital_status ||
      prev.min_age !== curr.min_age ||
      prev.max_age !== curr.max_age ||
      prev.income !== curr.income ||
      prev.manglik !== curr.manglik ||
      prev.verified_only !== curr.verified_only ||
      prev.sort_by !== curr.sort_by ||
      prev.caste !== curr.caste
    );
    if (nonQChanged) {
      load(0, curr);
    }
    prevFiltersRef.current = curr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.districts,
    filters.block,
    filters.religion,
    filters.education,
    filters.marital_status,
    filters.min_age,
    filters.max_age,
    filters.income,
    filters.manglik,
    filters.verified_only,
    filters.sort_by,
    filters.caste,
  ]);

  // Debounced search for text q
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      load(0, filters);
    }, 500);
    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const toggleDistrict = (districtName) => {
    setFilters((f) => {
      const exists = f.districts.includes(districtName);
      const districts = exists
        ? f.districts.filter((d) => d !== districtName)
        : [...f.districts, districtName];
      return { ...f, districts, block: ANY };
    });
  };

  const getAvailableBlocks = () => {
    if (filters.districts.length === 0) {
      let all = [];
      JHARKHAND_DISTRICTS.forEach((d) => { all = [...all, ...blocksFor(d)]; });
      return [...new Set(all)].sort();
    }
    let combined = [];
    filters.districts.forEach((d) => { combined = [...combined, ...blocksFor(d)]; });
    return [...new Set(combined)].sort();
  };

  const handleResetFilters = () => {
    const reset = { ...DEFAULT_FILTERS, min_age: defaultMinAge, verified_only: false };
    setFilters(reset);
    load(0, reset);
  };

  // Count active filters (excluding defaults)
  const activeFilterCount = [
    filters.districts.length > 0,
    filters.block !== ANY,
    filters.religion !== ANY,
    filters.education !== ANY,
    filters.marital_status !== ANY,
    filters.min_age !== defaultMinAge || filters.max_age !== 60,
    filters.caste.trim() !== "",
    filters.income !== ANY,
    filters.manglik !== ANY,
    filters.verified_only,
  ].filter(Boolean).length;

  const FiltersBody = (
    <div className="space-y-5">

      {/* Districts Multi-select */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
          Districts (Multi-select)
        </Label>
        <div className="mt-2 border border-border rounded-xl max-h-40 overflow-y-auto p-2.5 space-y-1.5 bg-muted/20">
          {JHARKHAND_DISTRICTS.map((d) => {
            const isChecked = filters.districts.includes(d);
            return (
              <label
                key={d}
                className={`flex items-center gap-2.5 text-sm cursor-pointer select-none rounded-lg px-2 py-1 transition-colors ${isChecked ? "bg-secondary/10 text-secondary font-semibold" : "text-foreground/80 hover:bg-muted"}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleDistrict(d)}
                  className="w-3.5 h-3.5 rounded accent-secondary border-border"
                />
                {d}
              </label>
            );
          })}
        </div>
        {filters.districts.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {filters.districts.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-1 text-[11px] bg-secondary/10 text-secondary border border-secondary/20 rounded-full px-2 py-0.5 font-medium"
              >
                {d}
                <button onClick={() => toggleDistrict(d)} className="hover:text-red-500 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Cascading Block Filter */}
      <FilterSel
        label="Block / Tehsil"
        value={filters.block}
        onChange={(v) => update("block", v)}
        items={getAvailableBlocks()}
        testid="flt-block"
        placeholder="Any block"
      />

      {/* Age Range Slider */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              Age Range
            </Label>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Legal Min Age in India: <span className="font-semibold text-primary">{defaultMinAge} yrs</span> ({defaultMinAge === 21 ? "Male Groom" : "Female Bride"})
            </p>
          </div>
          <span className="text-xs font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">
            {filters.min_age} – {filters.max_age} yrs
          </span>
        </div>
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-muted-foreground w-14 shrink-0">Min: {filters.min_age}</span>
            <input
              type="range"
              min={defaultMinAge}
              max={Math.min(59, filters.max_age - 1)}
              value={filters.min_age}
              onChange={(e) => {
                const val = Math.max(defaultMinAge, Math.min(Number(e.target.value), filters.max_age - 1));
                setFilters((f) => ({ ...f, min_age: val }));
              }}
              className="w-full accent-secondary h-1.5 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-muted-foreground w-14 shrink-0">Max: {filters.max_age}</span>
            <input
              type="range"
              min={Math.max(defaultMinAge + 1, filters.min_age + 1)}
              max={60}
              value={filters.max_age}
              onChange={(e) => {
                const val = Math.max(filters.min_age + 1, Math.min(60, Number(e.target.value)));
                setFilters((f) => ({ ...f, max_age: val }));
              }}
              className="w-full accent-secondary h-1.5 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Religion */}
      <FilterSel label="Religion" value={filters.religion} onChange={(v) => update("religion", v)} items={RELIGIONS} testid="flt-religion" />

      {/* Caste */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Caste</Label>
        <Input
          value={filters.caste}
          onChange={(e) => update("caste", e.target.value)}
          placeholder="e.g. Mahato, Rajput, Kurmi..."
          className="mt-1.5 rounded-xl text-sm"
          data-testid="flt-caste"
        />
      </div>

      {/* Education */}
      <FilterSel label="Education" value={filters.education} onChange={(v) => update("education", v)} items={EDUCATION} testid="flt-education" />

      {/* Marital Status */}
      <FilterSel label="Marital Status" value={filters.marital_status} onChange={(v) => update("marital_status", v)} items={MARITAL} testid="flt-marital" />

      {/* Income */}
      <FilterSel label="Income Range" value={filters.income} onChange={(v) => update("income", v)} items={INCOME_OPTIONS} testid="flt-income" />

      {/* Manglik */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Manglik</Label>
        <Select value={filters.manglik} onValueChange={(v) => update("manglik", v)}>
          <SelectTrigger className="mt-1.5 rounded-xl text-sm" data-testid="flt-manglik">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            <SelectItem value="No">Non-Manglik</SelectItem>
            <SelectItem value="Yes">Manglik</SelectItem>
            <SelectItem value="Anshik">Anshik / Partial</SelectItem>
            <SelectItem value="Don't Know">Don't Know</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Verified Only Toggle */}
      <div className="flex items-center justify-between rounded-xl border border-border p-3 bg-muted/20">
        <Label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <BadgeCheck className="w-4 h-4 text-emerald-600" /> Verified profiles only
        </Label>
        <Switch
          checked={filters.verified_only}
          onCheckedChange={(v) => update("verified_only", v)}
          data-testid="flt-verified-only"
        />
      </div>

      {/* Reset Button */}
      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => { handleResetFilters(); setSheetOpen(false); }}
          data-testid="flt-reset"
        >
          <X className="w-3.5 h-3.5 mr-1.5" /> Clear All Filters ({activeFilterCount})
        </Button>
      )}
    </div>
  );

  return (
    <div className="container-px py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="label-cap">Matches Search</span>
          <h1 className="font-heading text-4xl text-primary mt-1">Find your partner</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {loading ? "Searching..." : `${total} profile${total === 1 ? "" : "s"} found`}
            {activeFilterCount > 0 && (
              <button
                onClick={handleResetFilters}
                className="ml-2 text-xs text-secondary underline hover:no-underline"
              >
                Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
              </button>
            )}
          </p>
        </div>

        {/* Search + Sort + Mobile Filters */}
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          {/* Sort Dropdown */}
          <Select value={filters.sort_by} onValueChange={(v) => update("sort_by", v)}>
            <SelectTrigger className="w-44 rounded-full text-sm" data-testid="flt-sort">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search Bar */}
          <div className="relative flex-1 sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.q}
              onChange={(e) => update("q", e.target.value)}
              placeholder="Search name, city, occupation..."
              className="pl-9 pr-8 rounded-full"
              data-testid="search-input"
            />
            {filters.q && (
              <button
                onClick={() => update("q", "")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Mobile filter button */}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className={`lg:hidden rounded-full relative ${activeFilterCount > 0 ? "border-secondary text-secondary" : ""}`}
                data-testid="open-filters-mobile"
              >
                <SlidersHorizontal className="w-4 h-4 mr-1" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-secondary text-white text-[10px] flex items-center justify-center font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-3xl p-6">
              <SheetHeader className="mb-4">
                <SheetTitle className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-secondary" />
                  Search Filters
                  {activeFilterCount > 0 && (
                    <span className="text-xs bg-secondary text-white rounded-full px-2 py-0.5 font-semibold">
                      {activeFilterCount} active
                    </span>
                  )}
                </SheetTitle>
              </SheetHeader>
              <div className="pb-6">{FiltersBody}</div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active Filter Badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {filters.districts.map((d) => (
            <FilterBadge key={d} label={d} onRemove={() => toggleDistrict(d)} />
          ))}
          {filters.block !== ANY && <FilterBadge label={`Block: ${filters.block}`} onRemove={() => update("block", ANY)} />}
          {filters.religion !== ANY && <FilterBadge label={filters.religion} onRemove={() => update("religion", ANY)} />}
          {filters.education !== ANY && <FilterBadge label={filters.education} onRemove={() => update("education", ANY)} />}
          {filters.marital_status !== ANY && <FilterBadge label={filters.marital_status} onRemove={() => update("marital_status", ANY)} />}
          {filters.income !== ANY && <FilterBadge label={`Income: ${filters.income}`} onRemove={() => update("income", ANY)} />}
          {filters.manglik !== ANY && <FilterBadge label={`Manglik: ${filters.manglik}`} onRemove={() => update("manglik", ANY)} />}
          {filters.caste.trim() !== "" && <FilterBadge label={`Caste: ${filters.caste}`} onRemove={() => update("caste", "")} />}
          {(filters.min_age !== defaultMinAge || filters.max_age !== 60) && (
            <FilterBadge label={`Age: ${filters.min_age}–${filters.max_age}`} onRemove={() => setFilters((f) => ({ ...f, min_age: defaultMinAge, max_age: 60 }))} />
          )}
          {filters.verified_only && <FilterBadge label="✓ Verified Only" onRemove={() => update("verified_only", false)} />}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="card-soft p-5 sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-secondary" />
                <span className="font-heading text-lg text-primary font-semibold">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="text-[10px] bg-secondary text-white rounded-full px-1.5 py-0.5 font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              {activeFilterCount > 0 && (
                <button
                  onClick={handleResetFilters}
                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                >
                  Reset all
                </button>
              )}
            </div>
            {FiltersBody}
          </div>
        </aside>

        {/* Results */}
        <section className="lg:col-span-9">
          {loading ? (
            <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
              <span className="w-8 h-8 rounded-full border-2 border-t-transparent border-secondary animate-spin" />
              <span className="text-sm">Finding matches...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-20 border border-dashed rounded-2xl text-muted-foreground">
              <div className="text-4xl mb-3">🔍</div>
              <p className="font-medium text-base text-foreground">No matching profiles found</p>
              <p className="text-sm mt-1">Try broadening your filters or clearing some criteria.</p>
              {activeFilterCount > 0 && (
                <Button variant="outline" className="mt-4 rounded-full" onClick={handleResetFilters}>
                  <X className="w-3.5 h-3.5 mr-1.5" /> Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5">
              {results.map((p) => <ProfileTile key={p.user_id} p={p} />)}
            </div>
          )}

          {/* Pagination */}
          {!loading && total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 mt-10" data-testid="browse-pagination">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={page === 0}
                onClick={() => load(page - 1)}
                data-testid="page-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />Prev
              </Button>
              <span className="text-sm text-muted-foreground" data-testid="page-indicator">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={page + 1 >= totalPages}
                onClick={() => load(page + 1)}
                data-testid="page-next"
              >
                Next<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterBadge({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-secondary/10 text-secondary border border-secondary/20 rounded-full px-2.5 py-1 font-medium">
      {label}
      <button onClick={onRemove} className="hover:text-red-500 transition-colors ml-0.5">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function FilterSel({ label, value, onChange, items, testid, disabled, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="mt-1.5 rounded-xl text-sm" data-testid={testid}>
          <SelectValue placeholder={placeholder || "Any"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any</SelectItem>
          {items.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
